/**
 * Progress UI for NUT-09 token restore.
 *
 * Shows per-mint restore status with sats found and a total summary.
 * Presentational component -- no business logic, all data via props.
 *
 * Lives in src/components/ (shared) so both onboarding and cashu features
 * can import it without cross-feature import violations.
 *
 * Tier 1 - wallet restore flow.
 *
 * @module components/RestoreProgress
 */

import type { ReactNode } from 'react';
import type { MintRestoreProgress } from '@/lib/cashu-restore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatAmount } from '@/lib/amount-formatter';

/** Props for the RestoreProgress component. */
interface RestoreProgressProps {
  /** Per-mint progress data. */
  readonly progress: readonly MintRestoreProgress[];
  /** Whether the restore process is fully complete. */
  readonly isComplete: boolean;
  /** Total sats recovered across all mints. */
  readonly totalSats: number;
}

/** Status icon for a mint restore entry. */
function StatusIcon({ status }: { readonly status: MintRestoreProgress['status'] }): ReactNode {
  switch (status) {
    case 'scanning':
      return (
        <div
          className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"
          role="status"
          aria-label="Scanning"
        />
      );
    case 'complete':
      return (
        <svg
          className="h-5 w-5 text-success"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'skipped':
      return (
        <svg
          className="h-5 w-5 text-warning"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'error':
      return (
        <svg
          className="h-5 w-5 text-destructive"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
  }
}

/**
 * Displays token restore progress per mint with totals.
 *
 * Shows a heading that changes based on completion state, per-mint rows
 * with status icons and sats found, and a total summary when complete.
 *
 * @param props - Progress data, completion state, and total sats
 */
export function RestoreProgress({
  progress,
  isComplete,
  totalSats,
}: RestoreProgressProps): ReactNode {
  const heading = isComplete ? 'Tokens recovered' : 'Recovering your tokens...';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{heading}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3" aria-live="polite" aria-atomic="false">
          {progress.map((mint) => (
            <MintRow key={mint.mintUrl} mint={mint} />
          ))}

          {progress.length === 0 ? (
            <div className="flex items-center gap-3">
              <div
                className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"
                role="status"
                aria-label="Starting restore"
              />
              <span className="text-sm text-muted-foreground">Preparing to scan mints...</span>
            </div>
          ) : null}

          {isComplete ? (
            <div className="mt-2 border-t border-border pt-3">
              <p className="text-center text-base font-semibold">
                Total recovered: {formatAmount(totalSats, 'sats')}
              </p>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** A single mint row in the progress list. */
function MintRow({ mint }: { readonly mint: MintRestoreProgress }): ReactNode {
  return (
    <div className="flex items-center gap-3">
      <StatusIcon status={mint.status} />
      <div className="flex flex-1 flex-col">
        <span className="text-sm font-medium">{mint.mintLabel || mint.mintUrl}</span>
        {mint.status === 'complete' && mint.satsFound > 0 ? (
          <span className="text-xs text-muted-foreground">
            Found {formatAmount(mint.satsFound, 'sats')}
          </span>
        ) : null}
        {mint.status === 'complete' && mint.satsFound === 0 ? (
          <span className="text-xs text-muted-foreground">No tokens found</span>
        ) : null}
        {mint.error ? (
          <span className="text-xs text-destructive">{mint.error}</span>
        ) : null}
      </div>
    </div>
  );
}
